import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Table } from "../../../UI/Table";
import { TableHead } from "../../../UI/TableHead";
import { TableRow } from "../../../UI/TableRow";
import { TableCell } from "../../../UI/TableCell";
import { TableBody } from "../../../UI/TableBody";
import { Button } from "../../../UI/Button";
import { Box } from "../../../UI/Box";
import { Grid } from "../../../UI/Grid";
import { Typography } from "../../../UI/Typography";
import { Switch } from "../../../UI/Switch";
import { Modal } from "../../../components/Modal";
import { TextField } from "../../../UI/TextField";
import { ModalProvider } from "../../../contexts/ModalContext";
import { useModalContext } from "../../../contexts/ModalContext/context";
import useAuth from "../../login/AuthContext";
import { useSnackbar } from "../../../hooks/useSnackbar";
import {
  LoadPortfolioWithImportSettings,
  LoadPortfolioIntegrations,
  PorfolioIntegraion,
  NetworkWithImportSettings,
  GetEmailWhitelist,
  AddEmailWhitelist,
  EditEmailWhitelist,
  RemoveEmailWhitelist,
  EmailWhitelistEntry,
  UpdateNetworkImportSettings
} from "../../../services/PortfolioApi";

interface PortfolioNetworkIntegrationTabProps {
  portfolioId: string;
  networkId: string;
}

const StyledContainer = styled(Box)`
  padding: 16px 0;
  width: 100%;
`;

const SectionContainer = styled(Box)`
  background: #FAFAFB;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  width: 100%;
`;

const EmailLink = styled.a`
  color: #5E4DB2;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

// Basic email validation regex
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Helper function to format the lastRun date array
const formatLastRun = (lastRun: number[] | null): string => {
  if (!lastRun || lastRun.length < 6) {
    return '-';
  }
  const [year, month, day, hour, minute, second] = lastRun;
  const date = new Date(year, month - 1, day, hour, minute, second);
  return date.toLocaleString();
};

// Helper function to get integration type from portfolioImportSettingsId
const getIntegrationType = (portfolioImportSettingsId: number | undefined, portfolioIntegrations: PorfolioIntegraion[]): string => {
  if (!portfolioImportSettingsId || !portfolioIntegrations) {
    return '-';
  }
  const integration = portfolioIntegrations.find(integration => integration.id === portfolioImportSettingsId);
  return integration?.importType || '-';
};

export const PortfolioNetworkIntegrationTab: React.FC<PortfolioNetworkIntegrationTabProps> = ({ 
  portfolioId, 
  networkId 
}) => {
  return (
    <ModalProvider>
      <PortfolioNetworkIntegrationTabContent portfolioId={portfolioId} networkId={networkId} />
    </ModalProvider>
  );
};

const PortfolioNetworkIntegrationTabContent: React.FC<PortfolioNetworkIntegrationTabProps> = ({ 
  portfolioId, 
  networkId 
}) => {
  const { getAuthToken } = useAuth();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const { setIsOpen } = useModalContext();
  
  const [network, setNetwork] = useState<NetworkWithImportSettings | null>(null);
  const [portfolioIntegrations, setPortfolioIntegrations] = useState<PorfolioIntegraion[]>([]);
  const [emailWhitelist, setEmailWhitelist] = useState<EmailWhitelistEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [importEnabled, setImportEnabled] = useState(false);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEmailId, setEditingEmailId] = useState<number | null>(null);
  const [emailInput, setEmailInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const token = await getAuthToken();

      // First load network and integration data
      const [portfolioData, integrationsData] = await Promise.all([
        LoadPortfolioWithImportSettings(portfolioId, token),
        LoadPortfolioIntegrations(portfolioId, token)
      ]);

      let currentNetwork: NetworkWithImportSettings | undefined;
      let integrations: PorfolioIntegraion[] = [];

      if (portfolioData) {
        currentNetwork = portfolioData.networks.find(n => n.l1Id === Number(networkId));
        if (currentNetwork) {
          setNetwork(currentNetwork);
          setImportEnabled(currentNetwork.importSettings?.enabled || false);
        }
      }

      if (integrationsData?.settings) {
        integrations = integrationsData.settings;
        setPortfolioIntegrations(integrations);
      }

      // Check if this is an email integration and load whitelist if so
      const integrationType = getIntegrationType(currentNetwork?.importSettings?.portfolioImportSettingsId, integrations);
      const isEmailIntegration = integrationType.toLowerCase().includes('email');

      if (isEmailIntegration) {
        try {
          const whitelistData = await GetEmailWhitelist(Number(networkId), token);
          if (whitelistData) {
            setEmailWhitelist(whitelistData);
          }
        } catch {
          // Silently handle whitelist loading error for non-email networks
        }
      }
    } catch (error) {
      showErrorSnackbar("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [portfolioId, networkId]);

  const handleImportEnabledChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEnabled = event.target.checked;
    const networkSettingsId = network?.importSettings?.id;

    if (!networkSettingsId) {
      showErrorSnackbar('Unable to update import settings: missing configuration');
      return;
    }

    try {
      const token = await getAuthToken();
      const result = await UpdateNetworkImportSettings(portfolioId, Number(networkId), networkSettingsId, newEnabled, token);

      if (result.ok) {
        setImportEnabled(newEnabled);
        showSuccessSnackbar(`Import ${newEnabled ? 'enabled' : 'disabled'} successfully`);
      } else {
        showErrorSnackbar('Failed to update import settings');
      }
    } catch (error) {
      showErrorSnackbar('Failed to update import settings');
    }
  };

  const handleAddEmail = () => {
    setEmailInput("");
    setShowAddModal(true);
    setIsOpen(true);
  };

  const handleEditEmail = (entry: EmailWhitelistEntry) => {
    setEditingEmailId(entry.id);
    setEmailInput(entry.email);
    setShowEditModal(true);
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingEmailId(null);
    setEmailInput("");
    setIsOpen(false);
  };

  const handleSaveEmail = async () => {
    const trimmedEmail = emailInput.trim();

    if (!trimmedEmail) {
      showErrorSnackbar("Please enter an email address");
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      showErrorSnackbar("Please enter a valid email address");
      return;
    }

    try {
      setIsSaving(true);
      const token = await getAuthToken();

      if (showEditModal && editingEmailId !== null) {
        // Update existing email
        const result = await EditEmailWhitelist(Number(networkId), editingEmailId, trimmedEmail, token);
        if (result.ok) {
          // Update local state
          setEmailWhitelist(prev => prev.map(entry =>
            entry.id === editingEmailId ? { ...entry, email: trimmedEmail } : entry
          ));
          showSuccessSnackbar("Email updated successfully");
          handleCloseModal();
        } else {
          showErrorSnackbar("Failed to update email");
        }
      } else {
        // Add new email
        const result = await AddEmailWhitelist(Number(networkId), trimmedEmail, token);
        if (result.ok) {
          // Fetch the updated list to get the new ID
          const whitelistData = await GetEmailWhitelist(Number(networkId), token);
          if (whitelistData) {
            setEmailWhitelist(whitelistData);
          }
          showSuccessSnackbar("Email added successfully");
          handleCloseModal();
        } else {
          showErrorSnackbar("Failed to add email");
        }
      }
    } catch (error) {
      showErrorSnackbar("Failed to save email");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveEmail = async (entry: EmailWhitelistEntry) => {
    try {
      const token = await getAuthToken();
      const result = await RemoveEmailWhitelist(Number(networkId), entry.id, token);

      if (result.ok) {
        // Update local state by removing the entry
        setEmailWhitelist(prev => prev.filter(e => e.id !== entry.id));
        showSuccessSnackbar("Email removed successfully");
      } else {
        showErrorSnackbar("Failed to remove email");
      }
    } catch (error) {
      showErrorSnackbar("Failed to remove email");
    }
  };

  if (isLoading) {
    return <Typography>Loading...</Typography>;
  }

  const integrationType = getIntegrationType(network?.importSettings?.portfolioImportSettingsId, portfolioIntegrations);
  const isEmailIntegration = integrationType.toLowerCase().includes('email');
  const externalPropertyId = network?.importSettings?.externalIds?.join(', ') || '-';
  const lastImportRun = formatLastRun(network?.importSettings?.lastRun || null);

  return (
    <StyledContainer>
      {/* Network Details Table */}
      <SectionContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Network Name</TableCell>
              <TableCell>Integration Type</TableCell>
              <TableCell>Network ID</TableCell>
              <TableCell>External Property ID</TableCell>
              <TableCell>Import Enabled</TableCell>
              <TableCell>Last Import Run</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{network?.name || '-'}</TableCell>
              <TableCell>{integrationType}</TableCell>
              <TableCell>{networkId}</TableCell>
              <TableCell>{externalPropertyId}</TableCell>
              <TableCell>
                <Switch
                  checked={importEnabled}
                  onChange={handleImportEnabledChange}
                />
              </TableCell>
              <TableCell>{lastImportRun}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </SectionContainer>

      {/* Email Whitelist Section - Only show for email integrations */}
      {isEmailIntegration && (
        <SectionContainer>
          <Grid container justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Email</Typography>
            <Button appearance="primary" onClick={handleAddEmail}>
              Add email
            </Button>
          </Grid>
          <Table>
            <TableBody>
              {emailWhitelist.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>
                    <EmailLink href={`mailto:${entry.email}`}>{entry.email}</EmailLink>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Button appearance="default" onClick={() => handleEditEmail(entry)}>
                        Edit
                      </Button>
                      <Button appearance="default" onClick={() => handleRemoveEmail(entry)}>
                        Remove
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {emailWhitelist.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography color="textSecondary">No emails configured</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </SectionContainer>
      )}

      {/* Add/Edit Email Modal */}
      {(showAddModal || showEditModal) && (
        <Modal
          title={showEditModal ? "Edit Email" : "Add Email"}
          onClose={handleCloseModal}
          actions={
            <>
              <Button appearance="subtle" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleSaveEmail} isLoading={isSaving}>
                Save
              </Button>
            </>
          }
        >
          <Box px={3} py={2}>
            <TextField
              label="Email Address"
              value={emailInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailInput(e.target.value)}
              placeholder="Enter email address"
              width="100%"
            />
          </Box>
        </Modal>
      )}
    </StyledContainer>
  );
};

